import type { LucideIcon } from 'lucide-react-native'
import { create } from 'zustand'

export type Severity = 'info' | 'success' | 'warning' | 'error' | 'neutral'

export type NoticeIcon = LucideIcon | 'spinner'

export type NoticeAction = {
    label: string
    onPress: () => void
    dismissOnPress?: boolean
}

export type NoticeInput = {
    id?: string
    severity: Severity
    title?: string
    message: string
    icon?: NoticeIcon
    actions?: NoticeAction[]
    duration?: number
    dismissible?: boolean
}

export type Notice = Omit<NoticeInput, 'id'> & {
    id: string
    createdAt: number
}

type NoticeStore = {
    notices: Notice[]
    show: (input: NoticeInput) => string
    dismiss: (id: string) => void
    dismissAll: () => void
}

// Module-level timer registry: persists across store re-creations.
// Tests that exercise this store must call _resetNoticeStoreForTests() in beforeEach
// to clear pending timers; otherwise a previous test's setTimeout can fire mid-run.
const timers = new Map<string, ReturnType<typeof setTimeout>>()

function clearTimer(id: string) {
    const t = timers.get(id)
    if (t) {
        clearTimeout(t)
        timers.delete(id)
    }
}

function scheduleTimer(id: string, duration: number, getDismiss: () => (id: string) => void) {
    clearTimer(id)
    const t = setTimeout(() => {
        timers.delete(id)
        getDismiss()(id)
    }, duration)
    timers.set(id, t)
}

function generateId(): string {
    return `notice-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export const useNoticeStore = create<NoticeStore>()((set, get) => ({
    notices: [],
    show: input => {
        const id = input.id ?? generateId()
        const next: Notice = {
            ...input,
            id,
            createdAt: Date.now(),
        }
        set(state => {
            const idx = state.notices.findIndex(n => n.id === id)
            if (idx === -1) {
                return { notices: [...state.notices, next] }
            }
            const copy = state.notices.slice()
            copy[idx] = next
            return { notices: copy }
        })
        if (typeof input.duration === 'number' && input.duration > 0) {
            scheduleTimer(id, input.duration, () => get().dismiss)
        } else {
            clearTimer(id)
        }
        return id
    },
    dismiss: id => {
        clearTimer(id)
        set(state => {
            if (!state.notices.some(n => n.id === id)) return state
            return { notices: state.notices.filter(n => n.id !== id) }
        })
    },
    dismissAll: () => {
        for (const id of Array.from(timers.keys())) clearTimer(id)
        set({ notices: [] })
    },
}))

export function _resetNoticeStoreForTests() {
    for (const id of Array.from(timers.keys())) clearTimer(id)
    useNoticeStore.setState({ notices: [] })
}
